import React, { useEffect, useState } from "react";
import patientService from "../../../../../services/patient.service";
import { useEyeconxStore } from "../../../../../store/eyeconxStore";
import { toast } from "react-toastify";

const PatientInvalidDetails = ({ patient }) => {
  const clinicId = useEyeconxStore((s) => s.user.clinicId);

  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [selected, setSelected] = useState(new Set()); // selected log IDs
  const [selectAll, setSelectAll] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchInvalidLogs();
  }, [clinicId, patient?.id]);

  const fetchInvalidLogs = async () => {
    setLoading(true);
    setSelected(new Set());
    setSelectAll(false);

    try {
      const res = await patientService.getPatientInvalidData(
        clinicId,
        patient.id
      );
      setLogs(res.data || []);
      setNotFound(false);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
        setLogs([]);
      } else {
        toast.error("Failed to load invalid detail logs.");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    const updated = new Set(selected);

    if (updated.has(id)) updated.delete(id);
    else updated.add(id);

    setSelected(updated);
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelected(new Set());
      setSelectAll(false);
    } else {
      setSelected(new Set(logs.map((l) => l.id)));
      setSelectAll(true);
    }
  };

  const deleteSingle = async (id) => {
    try {
      await patientService.deletePatientInvalidData(clinicId,patient?.id, [id]);
      toast.success("Invalid detail removed.");
      fetchInvalidLogs();
    } catch {
      toast.error("Failed to delete log.");
    }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) {
      toast.info("No logs selected.");
      return;
    }

    try {
     
        await patientService.deletePatientInvalidData(clinicId, patient?.id,  Array.from(selected));
     

      toast.success("Selected logs removed.");
      fetchInvalidLogs();
    } catch {
      toast.error("Failed to delete selected logs.");
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 1: return { label: "Invalid Email", variant: "danger", icon: "bi-envelope-exclamation" };
      case 2: return { label: "Invalid Cell Phone", variant: "warning", icon: "bi-phone" };
      case 3: return { label: "Invalid Home Phone", variant: "secondary", icon: "bi-telephone-x" };
      default: return { label: "Unknown", variant: "dark", icon: "bi-question-circle" };
    }
  };

  if (loading) {
    return (
      <div className="card border-0 shadow-sm my-4 text-center py-5">
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">
          <i className="bi bi-exclamation-octagon me-2 text-danger"></i>
          Invalid Patient Details
        </h5>

        <div className="d-flex gap-2">
          {logs.length > 0 && (
            <>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={deleteSelected}
              >
                <i className="bi bi-trash me-1"></i> Delete Selected
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchInvalidLogs}
              >
                <i className="bi bi-arrow-repeat me-1"></i> Refresh
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card-body p-0">
        {notFound || logs.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-check-circle text-success display-4"></i>
            <p className="text-muted mt-3 mb-0">No invalid details found.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="ps-4" scope="col">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectAll}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th scope="col">Type</th>
                  <th scope="col">Detail</th>
                  <th scope="col">Date</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>

              <tbody>
                {logs.map((log) => {
                  const info = getTypeLabel(log.type);
                  return (
                    <tr key={log.id}>
                      <td className="ps-4">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selected.has(log.id)}
                          onChange={() => toggleSelect(log.id)}
                        />
                      </td>

                      <td>
                        <span className={`badge bg-${info.variant}`}>
                          <i className={`bi ${info.icon} me-1`}></i>
                          {info.label}
                        </span>
                      </td>

                      <td>
                        <strong>{log.detail}</strong>
                        <div className="text-muted small">
                          {log.patient.firstName} {log.patient.lastName}
                          {log.patient.preferredName && (
                            <> ({log.patient.preferredName})</>
                          )}
                        </div>
                      </td>

                      <td>{log.createdAt}</td>

                      <td className="pe-4">
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteSingle(log.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientInvalidDetails;
